/**
 * What a lump of "refused before any upstream" is actually made of.
 *
 * The scalar alone is not actionable, because the codes inside it call for
 * opposite responses and two of them are findings an operator would act on
 * today. Production 24h:
 *
 *   REQUEST_NOT_SUPPORTED  7,135   a client integration pointing at the wrong
 *                                  thing — theirs, and nothing to fix here
 *   INSUFFICIENT_BALANCE     497   paying customers being turned away. A
 *                                  REVENUE signal, invisible until now
 *   MODEL_NOT_AVAILABLE      349   we could not route it. OURS
 *   ACCOUNT_UNAVAILABLE        6
 *   INVALID_REQUEST            6
 *
 * Folded into one number, the 349 hide inside the 7,135 and the page reports
 * our own inability to serve as the gate working correctly.
 *
 * ── What the split has to make obvious ──
 *
 * Which side of the line each code falls on. `refusalCodes.ts` decides that
 * and explains why; this renders it as two visually distinct groups so
 * "excluded because it was not ours" cannot be mistaken for "counted against
 * us". The excluded ones are dimmed and marked; the counted ones carry the
 * `bad` tone, because they are failures the SKU is answerable for and they
 * are already inside its downstream rate.
 *
 * Absence is its own state and never renders as an empty list: a gateway that
 * cannot report the split says so, and the page states that nothing was
 * deducted rather than implying there was nothing to deduct.
 *
 * The MECHANISM is never named — no 防火墙 / admission-rejected. The counter
 * is structural; the console has the code and nothing more.
 */

import { useT } from '../../i18n/index.ts';
import { formatInteger } from './display.ts';
import { refusalCodeLabel } from './refusalCodes.ts';
import type { RefusalGroup } from './refusalCodes.ts';

interface Props {
  readonly groups: readonly RefusalGroup[] | null;
  readonly testId: string;
  /** Cap the list; the rest fold into a "+N more" tail. Zero means no cap. */
  readonly limit?: number;
}

export function RefusalBreakdown({ groups, testId, limit = 0 }: Props): JSX.Element | null {
  const t = useT();

  if (groups === null) {
    return (
      <p className="m-0 text-micro text-warn" data-testid={`${testId}-unknown`}>
        {t('health_perspective.refusal.unknown')}
      </p>
    );
  }
  if (groups.length === 0) return null;

  const shown = limit > 0 ? groups.slice(0, limit) : groups;
  const hidden = groups.length - shown.length;

  return (
    <ul
      className="m-0 flex list-none flex-wrap items-center gap-x-2 gap-y-1 p-0"
      data-testid={testId}
    >
      {shown.map((group) => (
        <li key={group.code} data-testid={`${testId}-${group.code}`}>
          <span
            className={
              group.expected
                ? 'inline-flex items-center gap-1 rounded border border-line px-1.5 py-0.5 text-micro text-paper-low'
                : 'inline-flex items-center gap-1 rounded border border-bad-line bg-bad-dim/40 px-1.5 py-0.5 text-micro text-bad'
            }
          >
            {t('health_perspective.refusal.entry', {
              label: refusalCodeLabel(group.code, t),
              count: formatInteger(group.count),
            })}
            <span className="text-paper-low">
              {t(
                group.expected
                  ? 'health_perspective.refusal.excluded'
                  : 'health_perspective.refusal.counted',
              )}
            </span>
          </span>
        </li>
      ))}
      {hidden > 0 ? (
        <li className="text-micro text-paper-low">
          {t('health_perspective.refusal.more', { count: formatInteger(hidden) })}
        </li>
      ) : null}
    </ul>
  );
}
